import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { eventsApi, getApiErrorMessage, toDE } from "../api";
import { DartsEvent } from "../types";
import { useGuardedAsyncAction } from "../hooks/useGuardedAsyncAction";

interface EventsContextValue {
  events: DartsEvent[];
  isLoading: boolean;
  error: string | null;
  fetchEvents: () => Promise<void>;
  lastFetchedAt: number | null;
}

const EventsContext = createContext<EventsContextValue | null>(null);

export const EventsProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [events, setEvents] = useState<DartsEvent[]>([]);
  const { isLoading, error, lastFetchedAt, run, setLastFetchedAt, setError } =
    useGuardedAsyncAction();

  const fetchEvents = useCallback(async () => {
    await run(
      async () => {
        const raw = await eventsApi.getAll();
        setEvents(raw.map(toDE));
      },
      { onErrorMessage: getApiErrorMessage },
    );
  }, [run]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
    } else {
      setEvents([]);
      setLastFetchedAt(null);
      setError(null);
    }
  }, [fetchEvents, isAuthenticated, setError, setLastFetchedAt]);

  return (
    <EventsContext.Provider
      value={{ events, isLoading, error, fetchEvents, lastFetchedAt }}
    >
      {children}
    </EventsContext.Provider>
  );
};

export const useEventsContext = (): EventsContextValue => {
  const ctx = useContext(EventsContext);
  if (!ctx)
    throw new Error("useEventsContext must be used within EventsProvider");
  return ctx;
};
